/**
 * Entitlements — user-level entitlement math over `userMetadata`, composed
 * with the Plan domain model. Owns credits and free-thread-allowance
 * decisions that were previously inline in components and helpers.
 *
 * NOT a singleton (the numbers change), so do NOT return fresh instances from
 * bare useSelector calls — use the memoized `selectEntitlements` from
 * `onboardingSelectors.ts`, which is stable while the metadata reference is.
 *
 * Roadmap: docs/domain-refactor-roadmap.md (Module 5). Linear: INH-5150.
 */
import { Plan } from "src/domain/plan";

export interface EntitlementsMetadata {
  subscription_type?: string | null;
  legal_review_credits_remaining?: number | null;
  legal_review_credits_total?: number | null;
  free_threads_remaining?: number | null;
}

export class Entitlements {
  readonly plan: Plan;
  /** Consultation credits left this cycle. Missing → 0 (matches old `?? 0` call sites). */
  readonly creditsRemaining: number;
  /**
   * Free-thread allowance left. `null` when the BE didn't send the field —
   * distinct from 0, which is a real "used up" signal (the old inline checks
   * were strict `=== 0` for exactly this reason).
   */
  readonly threadsRemaining: number | null;

  private constructor(metadata: EntitlementsMetadata) {
    this.plan = Plan.from(metadata.subscription_type);
    this.creditsRemaining = metadata.legal_review_credits_remaining ?? 0;
    this.threadsRemaining = metadata.free_threads_remaining ?? null;
    Object.freeze(this);
  }

  private static readonly ANONYMOUS = new Entitlements({});

  static from(metadata: EntitlementsMetadata | null | undefined): Entitlements {
    if (!metadata) return Entitlements.ANONYMOUS;
    return new Entitlements(metadata);
  }

  /** Consultations included per cycle by the plan (0 for free/anonymous/AI plan). */
  get includedConsultations(): number {
    return this.plan.includedConsultations;
  }

  /** Mirrors the old `tierActions.hasCredits`. */
  hasConsultationCredits(): boolean {
    return this.creditsRemaining > 0;
  }

  /**
   * The free-thread allowance is exhausted. Strict `=== 0`: an absent field
   * must NOT read as exhausted (old `useIsChatDisabled` semantics preserved).
   */
  isOutOfFreeThreads(): boolean {
    return this.threadsRemaining === 0;
  }
}
